import { Fragment, FunctionComponent, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import Container from 'components/GlobalStyles/Marketing/Container'
import RoundedButton from 'components/RoundedButton'
import FlaredArrow from 'components/SVG/FlaredArrow'
import StarIcon from 'components/SVG/Star'
import Spacer from 'components/Spacer'

import { addCommas } from 'utils/stringFormatting'

import { TestimonialDataA as TestimonialData } from 'data/MarketingData/TestimonialData'

const Background = styled.div`
  background: ${props => props.theme.colors.neutral25};
`

const Header = styled.h3`
  font-size: 20px;
  line-height: 28px;
  font-weight: 800;
  text-align: center;
  margin: 0;
  padding: 0;
  color: ${props => props.theme.colors.neutral900};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 28px;
    line-height: 38px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 32px;
    line-height: 44px;
  }
`

const SubHeader = styled.div`
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
  text-align: center;
  color: ${props => props.theme.colors.neutral400};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 18px;
    line-height: 25px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 20px;
    line-height: 28px;
  }
`

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    flex-direction: row;
  }
`

const Card = styled.div`
  display: flex;
  flex-basis: 30%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 24px;
  padding: 24px 48px;
  z-index: 1;
  box-shadow: 0px 32px 32px ${props => props.theme.colors.transparentGrey23Alpha25};
  background-color: ${props => props.theme.colors.white};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    justify-content: flex-start;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    padding: 48px 48px;
  }
  @media (min-width: ${props => props.theme.metrics.desktopLarge}px) {
    min-width: 346px;
  }
`

const PersonIconContainer = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: linear-gradient(115deg, rgba(255, 240, 162, 1) 0%, rgba(255, 205, 84, 1) 100%);
`

const PersonIcon = styled.img`
  height: 100%;
  width: 100%;
  border-radius: 50%;
  padding: 6px;
`

const RatingContainer = styled.div`
  svg {
    margin: 0 6px;
  }
`

const CustomerTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

interface ICustomerTextProps {
  color?: string
  italic?: boolean
}
const CustomerText = styled.div<ICustomerTextProps>`
  font-style: ${props => (props.italic ? 'italic' : 'normal')};
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: ${props => (props.color ? props.color : props.theme.colors.neutral900)};
  @media (min-width: ${props => props.theme.metrics.desktopLarge}px) {
    font-size: 16px;
    line-height: 22px;
  }
`

const BoldText = styled.span`
  font-weight: 800;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    flex-direction: row;
  }
`

interface IProps {
  scrollTo?: () => void
  customButton?: JSX.Element
}

const TestimonialsA: FunctionComponent<IProps> = ({ scrollTo, customButton }) => {
  const theme = useContext(ThemeContext)

  const campaignType = 'homepage-1'

  const testimonials = TestimonialData.filter(testimonial =>
    Array.isArray(testimonial.campaignType)
      ? testimonial.campaignType.indexOf(campaignType) > -1
      : testimonial.campaignType === campaignType,
  )

  const renderRightArrowIcon = () => (
    <FlaredArrow
      fill={theme.colors.blue500}
      width={15}
      height={15}
    />
  )

  return (
    <Background>
      <Spacer
        size={72}
        tabletSize={48}
        responsiveSize={32}
      />
      <Container>
        <Header>Happy Sellers</Header>
        <Spacer
          size={16}
          desktopSize={24}
          desktopLargeSize={24}
        />
        <SubHeader>Don&apos;t take it from us, take it from them!</SubHeader>
        <Spacer
          size={36}
          tabletSize={40}
          desktopSize={56}
          desktopLargeSize={56}
        />
        <CardsContainer>
          {testimonials.map((testimonial, index) => {
            const reviews = testimonial.review.split(testimonial.highlight)
            return (
              <Fragment key={testimonial.name}>
                {index > 0 && (
                  <Spacer
                    size={32}
                    tabletSize={24}
                    responsiveSize={24}
                  />
                )}
                <Card key={`${testimonial.name}-testimonial`}>
                  <PersonIconContainer>
                    <PersonIcon
                      src={testimonial.imgUrl}
                      alt={testimonial.name}
                    />
                  </PersonIconContainer>
                  <Spacer
                    size={24}
                    desktopSize={32}
                    desktopLargeSize={32}
                  />
                  <CustomerTextContainer>
                    <RatingContainer>
                      <StarIcon
                        fill={theme.colors.gold500}
                        width={18}
                        height={18}
                      />
                      <StarIcon
                        fill={theme.colors.gold500}
                        width={18}
                        height={18}
                      />
                      <StarIcon
                        fill={theme.colors.gold500}
                        width={18}
                        height={18}
                      />
                      <StarIcon
                        fill={theme.colors.gold500}
                        width={18}
                        height={18}
                      />
                      <StarIcon
                        fill={theme.colors.gold500}
                        width={18}
                        height={18}
                      />
                    </RatingContainer>
                    <Spacer size={16} />
                    <CustomerText>
                      {reviews[0]}
                      <BoldText>{testimonial.highlight}</BoldText>
                      {reviews[1]}
                    </CustomerText>
                    <Spacer size={16} />
                    <CustomerText color={theme.colors.neutral400}>
                      {`- ${testimonial.name}, sold their home ${
                        testimonial.location ? `in ${testimonial.location}` : ''
                      } and saved $${addCommas(testimonial.savings)} of their equity.`}
                    </CustomerText>
                  </CustomerTextContainer>
                </Card>
              </Fragment>
            )
          })}
        </CardsContainer>
        <Spacer
          size={56}
          tabletSize={40}
          responsiveSize={36}
        />
        <ButtonsContainer>
          {customButton || (
            <>
              <RoundedButton
                text="See More Reviews"
                href="https://www.zillow.com/profile/HomeLister/#reviews"
                externalLink
                background={theme.colors.blue500}
                hoverBackground={theme.colors.blue9}
                color={theme.colors.white}
                boxShadow={`0px 12px 16px ${theme.colors.transparentBlue4Alpha25}`}
                fontSize={12}
                radius={50}
                height={44}
                width={240}
              />
              <Spacer size={20} />
              <RoundedButton
                text="Get Started"
                background={theme.colors.white}
                borderColor={theme.colors.blue500}
                borderWidth={2}
                color={theme.colors.blue500}
                boxShadow={`0px 12px 16px ${theme.colors.transparentBlue4Alpha25}`}
                renderRightIcon={renderRightArrowIcon}
                fontSize={12}
                radius={50}
                height={44}
                width={240}
                onClick={scrollTo}
              />
            </>
          )}
        </ButtonsContainer>
      </Container>
      <Spacer
        size={72}
        tabletSize={48}
        responsiveSize={32}
      />
    </Background>
  )
}

export default TestimonialsA
